export const LEGAL_PAGE_KEYS = [
  'privacy-policy',
  'terms-of-service',
  'cookie-policy',
  'disclaimer',
  'about-us',
  'contact-us',
]

export const LEGAL_PAGES = {
  'privacy-policy': {
    title: 'Privacy Policy',
    eyebrow: 'Privacy & Data Use',
    description: 'Read how GamesNest handles anonymous analytics data, cookies, future advertising technologies, and user privacy rights under GDPR and CCPA.',
    intro: 'GamesNest is a global gaming news, guides, and reviews website. We do not require registration and we do not ask visitors to submit personal information just to browse the site. This policy explains what limited data may still be collected automatically, why it is used, and what choices you have.',
    sections: [
      {
        heading: 'What We Collect',
        paragraphs: [
          'We do not offer account registration and we do not intentionally collect personal information such as your name, phone number, or home address through normal site browsing.',
          'Like most websites, we may receive anonymous technical information automatically when you visit GamesNest. This can include your IP address, browser type, device type, approximate location based on IP, pages viewed, time spent on pages, referral source, and other basic visit behavior.',
        ],
      },
      {
        heading: 'How We Use Data',
        paragraphs: [
          'We use this anonymous information to understand which articles and sections are useful, improve site performance, detect abuse, and make editorial decisions about what content to publish next.',
          'We also use Google Analytics to measure traffic and visitor behavior in an aggregated way. Google Analytics may use cookies or similar technologies to help us understand site usage patterns.',
        ],
      },
      {
        heading: 'Cookies and Advertising',
        paragraphs: [
          'GamesNest uses cookies and similar technologies for analytics, site functionality, and user experience. In the future, we may also use advertising services such as Google AdSense. If that happens, Google and its partners may use cookies, including the DoubleClick cookie, to serve and measure ads based on visits to this site and other sites.',
          'You can disable cookies in your browser settings at any time. You can also manage or turn off personalized advertising through Google ad settings and related privacy tools provided by advertising partners.',
        ],
      },
      {
        heading: 'GDPR and CCPA Rights',
        paragraphs: [
          'If you are located in the European Economic Area, United Kingdom, or California, you may have rights related to access, deletion, correction, restriction, objection, or information about how data is used, depending on the laws that apply to you.',
          'Because GamesNest does not require user accounts and does not actively build personal visitor profiles, our ability to identify a specific visitor may be limited. Even so, we will review privacy requests in good faith and respond where legally required.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'If you have questions about this Privacy Policy or want to make a privacy-related request, please contact us at contact@gamesnest.org.',
        ],
      },
    ],
  },
  'terms-of-service': {
    title: 'Terms of Service',
    eyebrow: 'Site Terms',
    description: 'Review the simple terms that apply when you access and use GamesNest content, articles, guides, and reviews.',
    intro: 'By using GamesNest, you agree to these Terms of Service. We aim to keep them clear and readable. If you do not agree, please stop using the site.',
    sections: [
      {
        heading: 'Reference and Entertainment Only',
        paragraphs: [
          'GamesNest publishes gaming news, guides, reviews, and opinion-based content for reference and entertainment. We do not guarantee that every article is perfectly accurate, complete, or up to date at all times.',
          'Games, patches, balance updates, release plans, and platform policies can change quickly, so you should verify critical details through official sources when needed.',
        ],
      },
      {
        heading: 'Use at Your Own Risk',
        paragraphs: [
          'You use the site and any information on it at your own risk. Decisions you make based on our content are your responsibility.',
          'GamesNest is not responsible for losses, damages, or problems that may result from relying on our articles, reviews, recommendations, or linked resources.',
        ],
      },
      {
        heading: 'Prohibited Use',
        paragraphs: [
          'You may not attack, disrupt, scrape at scale, abuse, or attempt to interfere with the website or its infrastructure.',
          'You may not copy, republish, or mass reproduce our content without permission, especially for commercial use or automated content farming.',
        ],
      },
      {
        heading: 'Ownership and Changes',
        paragraphs: [
          'Unless otherwise stated, the content, branding, and editorial materials on GamesNest belong to the site owner or are used under applicable rights and fair editorial use.',
          'We may update these terms at any time. Continued use of the site after changes are posted means you accept the revised terms.',
        ],
      },
    ],
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    eyebrow: 'Cookies Explained',
    description: 'Learn what cookies are, why GamesNest uses them, and how you can control analytics or advertising-related cookies.',
    intro: 'This Cookie Policy explains what cookies are, how GamesNest uses them, and what options you have if you want to limit or disable them.',
    sections: [
      {
        heading: 'What Cookies Are',
        paragraphs: [
          'Cookies are small text files stored on your device when you visit a website. They help websites remember preferences, understand usage patterns, and improve the browsing experience.',
        ],
      },
      {
        heading: 'How We Use Cookies',
        paragraphs: [
          'GamesNest may use cookies for analytics, basic site functionality, and future advertising support. For example, Google Analytics may use cookies to help us understand which pages are visited and how users move through the site.',
          'If advertising features are added later, Google advertising technologies may also use cookies, including the DoubleClick cookie, to measure ads and support ad personalization.',
        ],
      },
      {
        heading: 'Your Choices',
        paragraphs: [
          'You can disable or delete cookies through your browser settings. You can also turn off personalized ads through Google ad settings or other industry privacy tools if advertising becomes active on the site.',
          'Blocking some cookies may affect how certain features work, but you can still browse most GamesNest content without enabling them.',
        ],
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    eyebrow: 'Important Notice',
    description: 'Understand the limits of GamesNest guides, reviews, third-party links, and AI-assisted content.',
    intro: 'GamesNest publishes gaming guides, reviews, news, and related editorial content. Everything on the site is provided for general informational purposes only.',
    sections: [
      {
        heading: 'No Guarantee of Accuracy',
        paragraphs: [
          'We do not guarantee that all information on GamesNest is fully accurate, complete, or current. Games can change quickly through updates, balance patches, store changes, or platform announcements.',
        ],
      },
      {
        heading: 'Use at Your Own Risk',
        paragraphs: [
          'Any action you take based on our guides, reviews, or recommendations is your own responsibility. GamesNest is not liable for any loss, damage, or inconvenience resulting from use of the site.',
        ],
      },
      {
        heading: 'Third-Party Links and AI Assistance',
        paragraphs: [
          'Some pages may link to third-party websites, videos, stores, or official sources. We are not responsible for third-party content, policies, or availability.',
          'Some content on GamesNest may be created or refined with AI assistance as part of our editorial workflow. That is one more reason readers should independently verify important details before relying on them.',
        ],
      },
    ],
  },
  'about-us': {
    title: 'About Us',
    eyebrow: 'About GamesNest',
    description: 'Learn what GamesNest is, why it exists, and what kind of gaming content it aims to publish for players around the world.',
    intro: 'GamesNest is a gaming site built for people who want fast, useful information about games without having to dig through noise. We cover news, guides, reviews, and practical updates across different platforms and genres.',
    sections: [
      {
        heading: 'Why We Made It',
        paragraphs: [
          'We genuinely enjoy games and the communities around them. The goal of GamesNest is simple: help players find the information they need, understand what matters, and get back to playing.',
          'That means we care about readable writing, useful summaries, and articles that feel grounded instead of overproduced.',
        ],
      },
      {
        heading: 'What You Can Expect',
        paragraphs: [
          'You will find gaming news, walkthrough-style guides, review content, tips, and other editorial pieces designed to help players make sense of updates, mechanics, and releases.',
          'GamesNest is not pretending to be a giant corporation or a massive newsroom. It is a focused site built around honest interest in games and helpful information.',
        ],
      },
    ],
  },
  'contact-us': {
    title: 'Contact Us',
    eyebrow: 'Get in Touch',
    description: 'Contact GamesNest for feedback, corrections, partnership questions, or general site inquiries.',
    intro: 'If you want to contact GamesNest, the best way is by email.',
    sections: [
      {
        heading: 'Email',
        paragraphs: [
          'You can reach us at contact@gamesnest.org.',
        ],
      },
      {
        heading: 'What to Contact Us About',
        paragraphs: [
          'Feel free to email us about feedback, factual corrections, partnership or collaboration questions, broken links, or other site-related issues.',
        ],
      },
    ],
  },
}

export function getLegalPage(key) {
  return LEGAL_PAGES[key] || null
}
